import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import USMap, { KIND_COLORS } from '../components/USMap';
import useSEO from '../hooks/useSEO';

const STATE_NAMES = { AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming' };

const FILTERS = [
    { key: 'all', label: 'All locations' },
    { key: 'dib', label: 'Dry Ice Bulls' },
    { key: 'partner', label: 'Authorized partners' },
];

const LocationCard = ({ l, active, onSelect }) => (
    <button onClick={() => onSelect(l.id)}
        className={`w-full text-left p-5 border transition-[border-color,background-color] ${active ? 'border-brand-primary bg-brand-primary/5' : 'border-neutral-200 bg-white hover:border-neutral-400'}`}
        data-testid={`location-card-${l.id}`}>
        <div className="flex items-center gap-2 label-caps text-[10px]" style={{ color: l.kind === 'dib' ? KIND_COLORS.dib : '#525252' }}>
            <span className="h-2 w-2 rounded-full" style={{ background: KIND_COLORS[l.kind] }} />
            {l.kind === 'dib' ? 'Dry Ice Bulls' : 'Authorized partner'}
        </div>
        <div className="mt-1 font-display font-black uppercase text-lg text-neutral-900">{l.name}</div>
        <div className="text-sm text-neutral-600 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{l.city}, {l.state}</div>
        {active && (
            <div className="mt-3 space-y-1.5 text-sm text-neutral-700" data-testid={`location-details-${l.id}`}>
                {l.description && <p className="text-neutral-600">{l.description}</p>}
                {l.services?.length > 0 && <p className="text-xs text-neutral-500">{l.services.join(' · ')}</p>}
                {l.phone && <a href={`tel:${l.phone}`} className="flex items-center gap-2 hover:text-brand-primary"><Phone className="h-3.5 w-3.5" />{l.phone}</a>}
                {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-2 hover:text-brand-primary"><Mail className="h-3.5 w-3.5" />{l.email}</a>}
                {l.website && <a href={l.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-brand-primary"><Globe className="h-3.5 w-3.5" />Website</a>}
                {!l.phone && !l.email && !l.website && (
                    <a href="tel:+19842010767" className="flex items-center gap-2 hover:text-brand-primary"><Phone className="h-3.5 w-3.5" />Contact via Dry Ice Bulls · (984) 201-0767</a>
                )}
            </div>
        )}
    </button>
);

const Filters = ({ filter, setFilter }) => (
    <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
                className={`px-5 py-3 label-caps border transition-colors ${filter === f.key ? 'bg-neutral-900 border-neutral-900 text-on-dark' : 'border-neutral-300 text-neutral-700 hover:border-neutral-900'}`}
                data-testid={`locations-filter-${f.key}`}>{f.label}</button>
        ))}
    </div>
);

export const PartnerMapSection = () => {
    const [locs, setLocs] = useState([]);
    const [filter, setFilter] = useState('all');
    const [activeId, setActiveId] = useState(null);
    useEffect(() => { api.get('/partner-locations').then((r) => setLocs(r.data)); }, []);
    const shown = useMemo(() => locs.filter((l) => filter === 'all' || l.kind === filter), [locs, filter]);
    const activeStates = useMemo(() => [...new Set(shown.map((l) => STATE_NAMES[l.state]).filter(Boolean))], [shown]);

    return (
        <div data-testid="partner-map-section">
            <Filters filter={filter} setFilter={setFilter} />
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-8 border border-neutral-200 bg-white p-4 md:p-8">
                    <USMap locations={shown} activeId={activeId} onSelect={setActiveId} activeStates={activeStates} />
                    <div className="mt-4 flex gap-6 text-xs text-neutral-600">
                        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: KIND_COLORS.dib }} />Dry Ice Bulls</span>
                        <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: KIND_COLORS.partner }} />Authorized partner / operator</span>
                    </div>
                </div>
                <div className="lg:col-span-4 space-y-3 lg:max-h-[640px] lg:overflow-y-auto" data-testid="locations-list">
                    {shown.length === 0 && <p className="text-neutral-500" data-testid="locations-empty">No locations listed yet.</p>}
                    {shown.map((l) => <LocationCard key={l.id} l={l} active={l.id === activeId} onSelect={setActiveId} />)}
                    <Link to="/partners/apply" className="flex items-center justify-between p-5 bg-brand-primary hover:bg-brand-primaryHover text-white font-bold label-caps transition-colors" data-testid="locations-become-partner">
                        Become an authorized partner <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

const PartnerLocations = () => {
    useSEO({ title: 'Dry Ice Bulls & Authorized Partner Locations — US Map', description: 'Find Dry Ice Bulls locations and authorized dry ice blasting partners and operators across the United States.' });
    return (
        <div data-testid="partner-locations-page">
            <section className="container-page pt-32 pb-10">
                <div className="label-caps text-brand-primary mb-6">/ Locations & partner network</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-7xl text-neutral-900">Find us near you</h1>
                <p className="mt-6 text-neutral-600 max-w-2xl">
                    Dry Ice Bulls locations and authorized partners and operators across the country. Each one is trained
                    on the equipment we sell. Tap a pin to see contact details.
                </p>
            </section>
            <section className="container-page pb-24"><PartnerMapSection /></section>
        </div>
    );
};

export default PartnerLocations;
